import {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react";

/** 字型：經典 / 手寫體 */
export type WallpaperFont = "classic" | "handwrite";
/** 字色：黑 / 白 */
export type WallpaperColor = "black" | "white";

/** 整個桌布客製流程共用的狀態（選擇 → 輸入文字 → 合成）。 */
export interface WallpaperFlowState {
	/** 選中的桌布 id；null = 尚未選擇 */
	wallpaperId: number | null;
	/** 使用者輸入的英文字母 */
	text: string;
	font: WallpaperFont;
	color: WallpaperColor;
	/** 合成完成後端回傳的圖片 URL（供分享頁用；存 sessionStorage 撐過 reload、關頁自動清） */
	resultImageUrl: string | null;
}

const DEFAULT_STATE: WallpaperFlowState = {
	wallpaperId: null,
	text: "",
	font: "classic",
	color: "black",
	resultImageUrl: null,
};

/** sessionStorage key：重整 / LIFF 內返回時把流程狀態續存回來。 */
const STORAGE_KEY = "wallpaper-flow";

interface WallpaperFlowContextValue extends WallpaperFlowState {
	/** 局部更新流程狀態 */
	update: (patch: Partial<WallpaperFlowState>) => void;
	/** 清空（例如完成或重新開始） */
	reset: () => void;
}

const WallpaperFlowContext = createContext<WallpaperFlowContextValue | null>(null);

function readStorage(): WallpaperFlowState {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (raw) {
			return { ...DEFAULT_STATE, ...JSON.parse(raw) };
		}
	} catch {
		// 無痕視窗 / 停用儲存時忽略，用預設值
	}
	return DEFAULT_STATE;
}

export function WallpaperFlowProvider({ children }: { children: ReactNode }) {
	const [state, setState] = useState<WallpaperFlowState>(readStorage);

	useEffect(() => {
		try {
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
		} catch {
			// 忽略寫入失敗
		}
	}, [state]);

	const value = useMemo<WallpaperFlowContextValue>(
		() => ({
			...state,
			update: (patch) => setState((prev) => ({ ...prev, ...patch })),
			reset: () => setState(DEFAULT_STATE),
		}),
		[state],
	);

	return (
		<WallpaperFlowContext.Provider value={value}>
			{children}
		</WallpaperFlowContext.Provider>
	);
}

/** 在 <WallpaperFlowProvider> 內取得流程狀態與更新函式。 */
export function useWallpaperFlow(): WallpaperFlowContextValue {
	const ctx = useContext(WallpaperFlowContext);
	if (!ctx) {
		throw new Error("useWallpaperFlow 必須在 <WallpaperFlowProvider> 內使用");
	}
	return ctx;
}
